"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import type { LangCode } from "@/types/message";
import type { SupportConversation, SupportMessage } from "@/types/support";
import { SupportMessageBubble } from "./SupportMessageBubble";
import { SupportComposer } from "./SupportComposer";
import { renderEmphasis } from "./renderEmphasis";

interface SupportChatTabProps {
  authed: boolean;
  userId: string | null;
  /** Soft keyboard visibility from the panel — keeps the latest message in
   *  view when the panel shrinks and lets the composer drop its safe-area
   *  padding while the keyboard covers the home indicator. */
  keyboardOpen: boolean;
  /** Customer's preferred reading language from `customer_profiles.
   *  preferred_language`. When set, it takes precedence over the URL
   *  [locale] for picking which `content_<lang>` column we render —
   *  ensures the customer always sees admin replies in their chosen
   *  language regardless of which page they happen to be on. */
  preferredLanguage: string | null;
  conversation: SupportConversation | null;
  messages: SupportMessage[];
  loading: boolean;
  onConversationCreated: (conv: SupportConversation) => void;
  onMessageSent: (msg: SupportMessage) => void;
  /** Used by the "Sign in" CTA so post-login the user lands back here. */
  loginReturnTo: string;
}

export function SupportChatTab({
  authed,
  userId,
  keyboardOpen,
  preferredLanguage,
  conversation,
  messages,
  loading,
  onConversationCreated,
  onMessageSent,
  loginReturnTo,
}: SupportChatTabProps) {
  const t = useTranslations("support.chat");
  const locale = useLocale() as LangCode;
  // Prefer the profile setting, fall back to the page locale for users who
  // haven't set one (or guests viewing pre-login state).
  const viewerLocale = (preferredLanguage ?? locale) as LangCode;
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to bottom on new message (or initial load).
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  // 키보드가 열려 패널이 줄어들 때 마지막 메시지를 유지한다.
  useEffect(() => {
    if (!keyboardOpen) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [keyboardOpen]);

  if (!authed) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-gray-50 p-6 text-center">
        <h3 className="text-base font-semibold text-gray-900">
          {t("loginRequiredTitle")}
        </h3>
        <p className="text-sm text-gray-600">{t("loginRequiredBody")}</p>
        <Link
          href={`/${locale}/login?returnTo=${encodeURIComponent(
            loginReturnTo,
          )}#support`}
          className="rounded-md bg-orange-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-orange-600"
        >
          {t("loginButton")}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-1 flex-col bg-gray-50">
      <div className="border-b border-red-200 bg-red-50 px-3 py-3">
        <p className="text-[14px] font-semibold text-red-800">
          {t("pinnedTitle")}
        </p>
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[13px] leading-relaxed text-red-700 marker:text-red-400">
          {t("pinnedBody")
            .split("\n")
            .map((line, idx) => (
              <li key={idx}>{renderEmphasis(line)}</li>
            ))}
        </ul>
      </div>
      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-3 py-4"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 py-8 text-center">
            <h3 className="text-[15px] font-semibold text-gray-900">
              {t("emptyTitle")}
            </h3>
            <p className="text-[13px] text-gray-500">{t("emptySubtitle")}</p>
          </div>
        ) : (
          messages.map((m) => (
            <SupportMessageBubble
              key={m.id}
              message={m}
              viewerLocale={viewerLocale}
              isMine={Boolean(userId) && m.sender_id === userId}
            />
          ))
        )}
        {loading && <p className="text-center text-xs text-gray-400">...</p>}
      </div>
      <SupportComposer
        conversation={conversation}
        keyboardOpen={keyboardOpen}
        onConversationCreated={onConversationCreated}
        onMessageSent={onMessageSent}
      />
    </div>
  );
}
